import {Component, DestroyRef, Input, OnInit, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {sendQuery} from "../../../../common/utils";
import {DiscountSelection, HarbourDuesDiscountForm, HarbourDuesExemption} from "../../types";
import {Liner} from "@portbase/bezoekschip-service-typescriptmodels";
import {VisitContext} from "../../../visit-context";

import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-discount-amsterdam',
  templateUrl: './discount-amsterdam.component.html',
  styleUrls: ['./discount-amsterdam.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DiscountAmsterdamComponent implements OnInit {
  @Input() discountForm: FormGroup<HarbourDuesDiscountForm>;
  @Input() exemption: FormControl<string | null>;
  @Input() selectedType: FormControl<DiscountSelection | null>;

  protected readonly DiscountSelection = DiscountSelection;

  linerOptions: Liner[] = [];

  discountTypeOptions: string[] = [
    "LINER", "AGRI_BULK"
  ];

  constructor(private destroyRef: DestroyRef) { }

  ngOnInit(): void {
    sendQuery('com.portbase.bezoekschip.common.api.visit.GetLinerServicesByCarrier', {
      eoriNumber: VisitContext.savedVisit.visitDeclaration.arrivalVoyage.carrier.customsId,
      portUnloCode: VisitContext.savedVisit.portOfCall.port.locationUnCode
    }).subscribe((response: Liner[]) => this.linerOptions = response || []);
    this.selectedType.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.toggleEnableDisable(value);
    });
    this.toggleEnableDisable(this.selectedType.value);
  }

  private toggleEnableDisable(value: DiscountSelection) {
    switch (value) {
      case DiscountSelection.NO_DISCOUNT:
        this.exemption.disable();
        this.discountForm.disable();
        break;
      case DiscountSelection.DISCOUNT:
        this.discountForm.enable();
        this.exemption.disable();
        break;
      case DiscountSelection.EXEMPTION:
        this.discountForm.disable();
        this.exemption.enable();
        break;
    }
  }

  exemptionArt81CodeFormatter = (exemption: string) => {
    switch (exemption) {
      case "TRAINING":
        return "A – Training ship for the navy, merchant navy or pilotage, irrespective of the flag under which it is sailing, " +
          "and every other Seagoing vessel in direct state service, provided that no transactions or transportation are " +
          "carried out in return for payment.";
      case "WAR":
        return "B – Warship provided that any Cargo is operated solely by military personnel.";
      case "TUG":
        return "C – Tug, only if and insofar as it is used for the purpose of normal assistance of Seagoing vessels in " +
          "the process of entering and leaving the Harbour.";
      case "CRUISE":
        return "D – Seagoing vessel for a period not exceeding sixty days, if the sole purpose of the visit to the port is " +
          "to prepare the vessel for its maiden voyage following construction, on the understanding that: " +
          "- the Harbourmaster is notified in writing in advance. " +
          "- the Harbourmaster is notified immediately once the vessel has been prepared for its maiden voyage. " +
          "- No Cargo is handled. " +
          "- The Seagoing vessel is permitted to sail in and out of the Port during this period on one or more test voyages.";
      case "REPAIR":
        return "F – Seagoing vessel that calls at the Port for a period not exceeding thirty days for the sole purpose of " +
          "having repairs carried out by an approved ship repair company outside of a Ship Repair Facility on the " +
          "understanding that the Harbourmaster is notified in writing in advance and that the Harbourmaster receives " +
          "an immediate written declaration detailing the nature of the work from the ship repair company once the work " +
          "has been completed.";
      case "CREW":
        return "G – Seagoing vessel that calls at the Port for a period not exceeding thirty days for the sole purpose of " +
          "exchanging crew and/or taking injured or deceased persons ashore, provided that the Harbourmaster is notified " +
          "in writing in advance. ";
      case "SCRAP":
        return "H – Seagoing vessel that calls at the Port for the sole purpose of demolition, provided that the ship " +
          "sails to the area where demolition work is to be carried out immediately upon arrival, and that the " +
          "Harbourmaster is notified in writing by the Client in advance of the demolition.";
      case "CONTRACTOR":
        return "K – Seagoing vessel which calls at the Port for the sole purpose of carrying out work commissioned by " +
          "Havenbedrijf Amsterdam N.V. or commissioned by the State, provided that this work takes place for the " +
          "purpose of construction, improvement or maintenance to the Port.";
    }
  }

  linerFormatter(liner: Liner) {
    return liner.code + " - " + liner.name;
  }

  isLiner() {
    return ["LINER"]
      .includes(this.discountForm.controls.discountType.value);
  }

  isLinerOrAgriBulk() {
    return ["AGRI_BULK", "LINER"]
      .includes(this.discountForm.controls.discountType.value);
  }

  discountSelected() {
    return this.selectedType.value == DiscountSelection.DISCOUNT;
  }

  exemptionSelected() {
    return this.selectedType.value == DiscountSelection.EXEMPTION;
  }

  formatShortSea = (b: boolean) => b ? 'Shortsea' : 'Deepsea';

  formatSelectionType = (selectionType : DiscountSelection) => {
    switch (selectionType) {
      case DiscountSelection.EXEMPTION:
        return 'Exemption'
      case DiscountSelection.DISCOUNT:
        return 'Discount(s)';
      default:
        return 'No discount';
    }
  }


  formatDiscountType(v: string) {
    switch (v) {
      case "AGRI_BULK":
        return "Agribulk frequency discount";
      case "LINER":
        return "Approved liner service discount";
    }
  }

  carrierName() {
    let carrierName = VisitContext.savedVisit.visitDeclaration.arrivalVoyage.carrier?.name;
    return carrierName ? `carrier ${carrierName}` : 'the carrier';
  }

  protected readonly HarbourDuesExemption = HarbourDuesExemption;
  protected readonly Object = Object;
}
